import React, { memo, ReactNode } from "react";

import useTranslations from "../../../../../hooks/useTranslations";

import useFormatDate from "../../../../../hooks/useFormatDate";
import UserAvatar from "../../../../utils/avatars/UserAvatar";
import { Tag } from "@chakra-ui/react";
import { AnswerFragment } from '../../../../../graphql/generated/definitions';

type AnswerRenderProps = {
  answer: AnswerFragment;
};

type AnswerProps = {
  answer: AnswerFragment;
  answerAs?: ReactNode;
  withComment?: boolean;
  comment?: ReactNode;
};

const AnswerRender: React.FC<AnswerRenderProps> = memo(({ answer }) => {
  const t = useTranslations();

  if (!answer || !answer.value) {
    return <p className="text-xs">{t("reports.noAnswer")}</p>;
  }

  switch (answer.type) {
    case "AnswerEvaluation":
    case "AnswerSkill":
    case "AnswerSelectorSingle":
      return (
        <>
          {answer.value.selector ? (
            <Tag>
              {
                answer.question.options.find(
                  (o) => o.value === answer.value.selector
                )?.label
              }
            </Tag>
          ) : null}
        </>
      );
    case "AnswerSelectorMultiple":
      return (
        <>
          {answer.value.selector.length > 0
            ? answer.value.selector.map((s) => (
                <Tag key={s}>
                  {answer.question.options.find((o) => o.value === s)?.label}
                </Tag>
              ))
            : null}
        </>
      );
    case "AnswerText":
      return answer.value;
    default:
      return <p className="text-xs">{t("reports.noAnswer")}</p>;
  }
});

const Answer: React.FC<AnswerProps> = ({ answer, withComment, comment }) => {
  const t = useTranslations();

  const formattedCreatedAt = useFormatDate({ date: answer.createdAt });

  return (
    <div className="flex flex-col col-span-1 text-sm bg-white rounded shadow text-dark-100">
      <div className="flex items-center px-6 py-3 space-x-4 border-b border-light-300 bg-light-0">
        <UserAvatar
          size={6}
          src={answer.user.avatar}
          alt={`${answer.user.name} profile picture`}
        />
        <div className="flex justify-between flex-grow font-semibold text-primary-700">
          {answer.user.name}
          <span className="ml-1 text-xs font-normal text-light-900">
            {formattedCreatedAt}
          </span>
        </div>
      </div>
      <div className="flex flex-wrap px-6 py-4 space-x-2 text-sm font-normal text-dark-500">
        <AnswerRender answer={answer} />
      </div>
      {answer.timeSpent > 0 && (
        <div className="px-6 pb-4">
          <h6 className="text-xs font-medium text-light-900">
            {t("reports.timeSpentInHour")}:
          </h6>
          <div className="flex flex-wrap space-x-2 text-xs font-normal text-dark-300">
            {answer.timeSpent}
          </div>
        </div>
      )}
      {withComment && (
        <div className="px-6 pb-4">
          <h6 className="text-xs font-medium text-light-900">
            {t("reports.comments")}:
          </h6>
          <div className="flex flex-wrap space-x-2 text-xs font-normal text-dark-300">
            {comment ? comment : <em> {t("reports.noComments")}</em>}
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(Answer);
